import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Play, Pause, RectangleHorizontal, RectangleVertical, Video, Link2, Music,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { BACKEND_URL } from '@meno/ui/lib/api'
import PdfThumbnail from '@/components/PdfThumbnail'

// Shared content preview used by the playlist / stop-screen / audio-playlist
// pages. It renders a 16:9 (landscape) or 9:16 (portrait) frame that cycles
// through the given items with a progress bar + "X / Y" badge, plus a
// Play/Preview button and (for visual content) an orientation toggle. `mode`
// picks the visual frame vs the audio "now playing" card (no orientation).
export type PreviewKind = 'image' | 'video' | 'pdf' | 'link' | 'audio'
export type PreviewItem = {
  key: string
  name: string
  kind: PreviewKind
  url?: string
  link?: string
  durationSec: number
  muted?: boolean
  thumb?: string
}

function absUrl(u?: string) {
  if (!u) return ''
  return u.startsWith('http') ? u : `${BACKEND_URL}${u}`
}

export default function PreviewPanel({
  items, mode = 'visual', overlay,
}: {
  items: PreviewItem[]
  mode?: 'visual' | 'audio'
  // Rendered absolutely inside the visual frame, above the content — the
  // stop-screen page uses this to paint its CTA button on the preview.
  overlay?: React.ReactNode
}) {
  const { t } = useTranslation()
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape')
  const [previewing, setPreviewing] = useState(false)
  const [idx, setIdx] = useState(0)
  const timerRef = useRef<number | null>(null)

  const clear = () => { if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null } }
  const stop = () => { clear(); setPreviewing(false); setIdx(0) }
  const advance = (from: number) => { if (from + 1 >= items.length) stop(); else setIdx(from + 1) }
  const start = () => {
    if (items.length === 0) return
    if (previewing) { stop(); return }
    setIdx(0); setPreviewing(true)
  }
  const current = previewing ? (items[idx] ?? null) : (items[0] ?? null)

  // Videos + audio self-advance via onEnded; everything else runs on a timer
  // sized to the item's duration.
  useEffect(() => {
    if (!previewing) return
    const it = items[idx]
    if (!it) { stop(); return }
    if (it.kind === 'video' || it.kind === 'audio') return
    timerRef.current = window.setTimeout(() => advance(idx), Math.max(1, it.durationSec) * 1000)
    return () => clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewing, idx, items.length])
  useEffect(() => () => clear(), [])

  const progressMs = current ? Math.max(1, current.durationSec) * 1000 : 0

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="flex-1 flex items-center justify-center min-h-0">
        <PreviewFrame
          mode={mode}
          orientation={orientation}
          item={current}
          previewing={previewing}
          onEnded={() => advance(idx)}
          progressKey={idx}
          progressMs={progressMs}
          currentIndex={idx}
          totalItems={items.length}
          frameOverlay={overlay}
        />
      </div>

      <div className="pt-4 mt-4 flex items-center justify-between">
        <Button
          variant="ghost"
          disabled={items.length === 0}
          onClick={start}
          className="gap-2 h-10 px-4 bg-muted/40 text-muted-foreground hover:bg-muted disabled:opacity-50"
        >
          {previewing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          {previewing ? t('playlist.stopPreview', 'Stop Preview') : t('playlist.playPreview', 'Play Preview')}
        </Button>
        {mode === 'visual' && (
          <div className="flex items-center gap-1">
            <OrientationButton
              active={orientation === 'landscape'}
              onClick={() => setOrientation('landscape')}
              icon={RectangleHorizontal}
              label={t('playlist.landscape', 'Landscape')}
            />
            <OrientationButton
              active={orientation === 'portrait'}
              onClick={() => setOrientation('portrait')}
              icon={RectangleVertical}
              label={t('playlist.portrait', 'Portrait')}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function PreviewFrame({
  mode, orientation, item, previewing, onEnded, progressKey, progressMs, currentIndex, totalItems, frameOverlay,
}: {
  mode: 'visual' | 'audio'
  orientation: 'landscape' | 'portrait'
  item: PreviewItem | null
  previewing: boolean
  onEnded: () => void
  progressKey: number
  progressMs: number
  currentIndex: number
  totalItems: number
  frameOverlay?: React.ReactNode
}) {
  const { t } = useTranslation()
  const isAudio = mode === 'audio'
  const aspect = orientation === 'landscape' ? 'aspect-video' : 'aspect-[9/16]'
  const widthClass = orientation === 'landscape' ? 'w-full max-w-xl' : 'h-full max-h-[60vh] aspect-[9/16]'
  const frameClass = isAudio
    ? 'relative w-full max-w-xl aspect-video rounded-xl border-2 border-border bg-muted/30 overflow-hidden flex flex-col items-center justify-center gap-3'
    : `relative ${orientation === 'landscape' ? widthClass : ''} ${orientation === 'portrait' ? widthClass : aspect} rounded-xl border-2 border-border bg-muted/30 overflow-hidden flex items-center justify-center`

  const keyframes = `@keyframes meno-preview-progress { from { width: 0% } to { width: 100% } }`
  const overlay = previewing && totalItems > 0 && (
    <>
      <style>{keyframes}</style>
      <div className="absolute top-2 end-2 z-10 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-black/55 text-white backdrop-blur-sm">
        {currentIndex + 1} / {totalItems}
      </div>
      <div
        key={progressKey}
        className="absolute bottom-0 start-0 h-1 bg-primary"
        style={{ animation: `meno-preview-progress ${progressMs}ms linear forwards` }}
      />
    </>
  )

  if (!item) {
    return (
      <div className={frameClass}>
        <p className="text-base font-medium text-muted-foreground/80">
          {t('playlist.noPreview', 'No content to preview')}
        </p>
        {!isAudio && frameOverlay}
      </div>
    )
  }

  // Audio "now playing" card — a music tile + track name, with the audio
  // element driving playback (and onEnded advancing the sequence).
  if (isAudio) {
    return (
      <div className={frameClass}>
        <div className="w-16 h-16 rounded-2xl bg-pink-500/15 flex items-center justify-center">
          <Music className="w-8 h-8 text-pink-500" />
        </div>
        <p className="text-sm font-medium text-foreground px-6 text-center truncate max-w-[80%]">{item.name}</p>
        {previewing && item.url && (
          <audio src={absUrl(item.url)} autoPlay muted={item.muted} onEnded={onEnded} />
        )}
        {overlay}
      </div>
    )
  }

  if (item.kind === 'link') {
    return (
      <div className={frameClass}>
        <div className="flex flex-col items-center gap-2 text-center px-6">
          <Link2 className="w-14 h-14 text-teal-600" />
          <p className="text-sm font-medium text-foreground">{item.name}</p>
          <p className="text-[11px] text-muted-foreground break-all max-w-xs">{item.link}</p>
        </div>
        {frameOverlay}
        {overlay}
      </div>
    )
  }

  const url = absUrl(item.url)
  return (
    <div className={frameClass}>
      {item.kind === 'image' && <img src={url} alt={item.name} className="w-full h-full object-contain" />}
      {item.kind === 'video' && previewing && (
        <video src={url} autoPlay muted={item.muted} playsInline onEnded={onEnded} className="w-full h-full object-contain" />
      )}
      {item.kind === 'video' && !previewing && item.thumb && <img src={item.thumb} alt={item.name} className="w-full h-full object-contain" />}
      {item.kind === 'video' && !previewing && !item.thumb && <Video className="w-14 h-14 text-purple-400" />}
      {item.kind === 'pdf' && <PdfThumbnail url={url} targetWidth={480} fit="contain" className="w-full h-full" />}
      {frameOverlay}
      {overlay}
    </div>
  )
}

function OrientationButton({
  active, onClick, icon: Icon, label,
}: {
  active: boolean
  onClick: () => void
  icon: typeof RectangleHorizontal
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-md transition-colors ${
        active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      <Icon className="w-6 h-6" />
      <span className="text-[11px] font-medium">{label}</span>
    </button>
  )
}
